/*---------------------------------------------------------------------------*
 *  APP
 *---------------------------------------------------------------------------*
 *
 *  Main script for the application.
 *
 *  @package    Squire
 *  @author     PubliOne (Luca Rosaldi)
 *  @author_uri https://publione.it
 *
 */

( function ( undefined ) {
  "use strict";

  /**
   * DOM elements.
   * @type {Node}
   */
  const htmlEl = document.documentElement;
  const bodyEl = document.body;
  const navEl  = document.querySelector( '.p-navbar' );

  /**
   * Add a class to <html> element which indicates that the user is tabbing.
   *
   * @param  {Event} event
   * @return {void}
   */
  const addUserIsTabbingClass = ( event ) => {
    if ( event.keyCode === 9 ) {  // tab key
      htmlEl.classList.add( 'user-is-tabbing' );
      window.removeEventListener( 'keydown', addUserIsTabbingClass );
    }
  };

  /**
   * Collection of UI Modules.
   * @type {Object}
   */
  const Modules = {};

  /**
   * Check whether library is defined in the window scope.
   *
   * @param  {String} name
   * @return {Bool}
   */
  const libraryExists = ( name ) => typeof window[ name ] !== 'undefined';

  /**
   * Load a module.
   *
   * @param  {String} name     Name of the module
   * @param  {String} selector Selector for elements on which to call the module
   * @return {void}
   */
  const loadModule = ( name, selector, options ) => {
    if ( typeof Modules[ name ] === 'undefined' ) {
      return;
    }

    if ( typeof selector === 'undefined' ) {
      Modules[ name ]();
      return;
    }

    [].forEach.call( document.querySelectorAll( selector ), ( elem, i ) => {
      elem[ name ] = Modules[ name ]( elem, options, i );
    });
  };

  /**
   * Open site offcanvas navigation.
   *
   * @return {void}
   */
  const openNav = () => {
    bodyEl.classList.add( 'nav-is-open' );
  }

  /**
   * Close site offcanvas navigation.
   *
   * @return {void}
   */
  const closeNav = () => {
    bodyEl.classList.remove( 'nav-is-open' );
  }

  /**
   * Initialize Scrollstep module.
   *
   * @uses scrollama.js
   *
   * @return {scrollama|null}
   */
  Modules.Scrollstep = () => {
    if ( ! libraryExists( 'scrollama' ) || ! ( 'IntersectionObserver' in window ) ) return;
    htmlEl.classList.add( 'js-module-scrollstep' );

    const options = {
      step: '[data-scrollstep]',
      offset: 0.65,
      order: true,
      once: true,
      debug: false
    };

    return new scrollama().setup( options ).onStepEnter( ( step ) => {
      step.element.classList.add( 'is-in-viewport' )
    });
  };

  /**
   * Initialize Headroom module.
   *
   * @uses Headroom
   *
   * @return {Headroom|null}
   */
  Modules.Headroom = ( elem ) => {
    if ( ! libraryExists( 'Headroom' ) ) return;
    htmlEl.classList.add( 'js-module-headroom' );

    elem.Headroom = new Headroom( elem, {
      offset: elem.offsetHeight,
      tolerance: {
        up: 10,
        down: 0
      },
      classes: {
        initial:   '',
        pinned:    'js-pinned',
        unpinned:  'js-unpinned',
        top:       'js-top',
        notTop:    'js-not-top',
        bottom:    'js-bottom',
        notBottom: 'js-not-bottom'
      }
    });
    elem.Headroom.init();
  };

  /**
   * Initialize Carousel module.
   *
   * Can be configured with data-attributes:
   *
   * [data-carousel-nav]        {Boolean} default true
   * [data-carousel-controls]   {Boolean} default true
   * [data-carousel-draggable]  {Boolean} default true
   * [data-carousel-align]      {String}  default 'center'
   * [data-carousel-attraction] {Float}   default 0.025
   * [data-carousel-friction]   {Float}   default 0.28
   * [data-carousel-startfrom]  (Integer) default 1
   * [data-carousel-loop]       {Boolean} default true
   * [data-carousel-autoplay]   {Integer} Milliseconds, default 0
   * [data-carousel-navfor]     {String}  Selector, default ''
   *
   * @uses Flickity.js
   *
   * @param  {Node}          elem
   * @param  {Object}        options
   * @return {Flickity|void}
   */
  Modules.Carousel = ( elem, options ) => {
    if ( ! libraryExists( 'Flickity' ) ) return;
    htmlEl.classList.add( 'js-module-carousel' );

    // init only if at least two elements are present
    if ( elem.childElementCount < 2 ) {
      return;
    }

    const attrs = {
      nav: elem.dataset.carouselNav,
      controls: elem.dataset.carouselControls,
      draggable: elem.dataset.carouselDraggable,
      align: elem.dataset.carouselAlign,
      fade: elem.dataset.carouselFade,
      attraction: elem.dataset.carouselAttraction,
      friction: elem.dataset.carouselFriction,
      initialIndex: elem.dataset.carouselStartfrom,
      loop: elem.dataset.carouselLoop,
      autoplay: elem.dataset.carouselAutoplay,
      navfor: elem.dataset.carouselNavfor,
      scrollTo: elem.dataset.carouselScrollto,
    };

    // extend default carousel options
    options = Object.assign({

      pageDots: attrs.nav !== 'false',
      prevNextButtons: attrs.controls !== 'false',
      draggable: ( attrs.draggable !== 'false' ) ? '>1' : false,
      cellAlign: attrs.align || 'center',
      fade: attrs.fade === 'true',
      selectedAttraction: ( attrs.attraction ) ? parseFloat( attrs.attraction ) : 0.025,
      friction: ( attrs.friction ) ? parseFloat( attrs.friction ) : 0.28,
      initialIndex: attrs.initialIndex - 1,
      wrapAround: attrs.loop !== 'false',
      autoPlay: !!attrs.autoplay,
      asNavFor: attrs.navfor || '',
      sync: attrs.navfor || '',

      adaptiveHeight: false,
      contain: true,
      groupCells: false,
      imagesLoaded: true,
      lazyLoad: true,
      pauseAutoPlayOnHover: true,
      rightToLeft: htmlEl.getAttribute( 'dir' ) === 'rtl',
      watchCSS: true

    }, options );

    var flickity = new Flickity( elem, options );

    // scrollstep compatibility
    flickity.on( 'select', () => {
      flickity.selectedElement.classList.add( 'is-in-viewport' );
    });
    flickity.on( 'settle', () => {
      flickity.slides.forEach( ( slide ) => {
        slide = slide.cells[0].element;
        if ( slide !== flickity.selectedElement ) {
          slide.classList.remove( 'is-in-viewport' );
        }
      });
    });

    // scroll to specific element when slide has changed
    if ( attrs.scrollTo ) {
      const target = document.querySelector( attrs.scrollTo );
      if ( target ) {
        flickity.on( 'change', function( index ) {
          target.scrollIntoView( { behavior: "smooth" } );
        });
      }
    }

    // prevent page jump when clicking on navigation links
    if ( options.asNavFor ) {
      elem.addEventListener( 'click', function( event ) {
        if ( event.currentTarget != flickity.selectedElement ) {
          event.preventDefault();
        }
      });
    }

    return flickity;
  };

  /**
   * Initialize all modules.
   */
  const modulesInit = () => {
    loadModule( 'Scrollstep', '[data-scrollstep]' );
    loadModule( 'Headroom', '[data-headroom]' );
    loadModule( 'Carousel', '[data-carousel]' );
  };
  modulesInit();

  /**
   * Add ready class to <body> when app has loaded.
   */
  window.addEventListener( 'load', () => {
    bodyEl.classList.remove( 'dom-is-loading' );
    bodyEl.classList.add( 'dom-is-ready' );
  });

  /**
   * Close offcanvas navigation when a link inside is clicked.
   */
  [].forEach.call( navEl.querySelectorAll( 'a' ), ( link ) => {
    link.addEventListener( 'click', closeNav );
  });

  /**
   * Add a custom class to <html> when user is using tabs to navigate the page.
   */
  window.addEventListener( 'keydown', addUserIsTabbingClass );

})();
